<template>
    <div :class="className">
        <!-- We override NebulaFieldGroup's default label rendering to conditionally add a popover tooltip. -->
        <div v-if="hasTooltipContent" class="profile-form-field__label-row">
            <NebulaLabel :labelText="$t(inputGroupData.display)" :inputId="inputId" :isRequired="inputGroupData.required" />
            <NebulaPopover :id="`${name}-guidance`" placement="bottom" show-close-button>
                <template #trigger>
                    <NebulaButton
                        :id="`nebula-popover-trigger__${name}-guidance`"
                        :aria-label="$t('%(name)s guidance', { name })"
                        icon-left="information"
                        size="s"
                        type="flat"
                    />
                </template>
                <template #content>
                    <slot name="tooltip-content" />
                </template>
            </NebulaPopover>
        </div>

        <NebulaFieldGroup
            :inputId="inputId"
            :labelText="hasTooltipContent ? null : $t(inputGroupData.display)"
            :isRequired="inputGroupData.required"
            :helperText="helperText"
        >
            <template v-slot:formElement>
                <template v-if="isSelect">
                    <NebulaSelect class="careers-form-select" :status="inputGroupData.status">
                        <template v-slot:select>
                            <select
                                :disabled="disabled"
                                class="nebula-select__element"
                                :id="inputId"
                                :name="name"
                                :key="inputGroupData.value"
                                :aria-describedby="helperId"
                                v-model="inputGroupData.value"
                                @change="selectChange()"
                            >
                                <NebulaSelectOption text="Select" value="" />
                                <slot name="customOptions">
                                    <NebulaSelectOption v-for="(option, index) in selectOptions" v-bind="option" :key="index" />
                                </slot>
                            </select>
                        </template>
                    </NebulaSelect>
                </template>
                <template v-else-if="inputType == 'multi-select'">
                    <NebulaVueSelect
                        v-if="showMultiSelect"
                        class="profile-form-field__vue-select"
                        :options="multiSelectOptions"
                        multiple
                        :value="multiSelectValue"
                        :placeholder="placeholder"
                        :status="inputGroupData.status"
                        size="m"
                        :reduce="reduce"
                        @change-selected="multiSelectTracker"
                    />
                </template>
                <template v-else-if="inputType == 'check-box'">
                    <div class="nebula-checkbox-group tnc__group">
                        <NebulaCheckbox :status="inputGroupData.status">
                            <template v-slot:checkbox>
                                <input
                                    class="nebula-checkbox"
                                    :id="name"
                                    :name="name"
                                    type="checkbox"
                                    v-model="inputGroupData.value"
                                    @input="inputHandler(!inputGroupData.value)"
                                />
                            </template>
                        </NebulaCheckbox>
                        <slot name="checkbox-label" />
                    </div>
                </template>
                <template v-else-if="inputType == 'datepicker'">
                    <NebulaDatepicker
                        :disabled="disabled"
                        :customClass="datepickerClass()"
                        :id="inputId"
                        v-model="inputGroupData.value"
                        :setDate="inputGroupData.value"
                        @date-change="validate()"
                    />
                    <NebulaIcon symbolId="calendar-add" class="profile-form-field__calendar-icon" />
                </template>
                <template v-else-if="inputType == 'timepicker'">
                    <input
                        type="time"
                        class="nebula-input nebula-input--m"
                        :class="{
                            'nebula-input--error': inputGroupData.status === 'error',
                            'nebula-input--warning': inputGroupData.status === 'warning',
                            'nebula-input--success': inputGroupData.status === 'success',
                        }"
                        :disabled="disabled"
                        :id="inputId"
                        :aria-describedby="helperId"
                        v-model="inputGroupData.value"
                        @change="validate()"
                    />
                </template>
                <template v-else>
                    <component
                        :disabled="disabled"
                        :is="inputComponent"
                        :class="inputType === 'textarea' && 'profile-form-field__textarea'"
                        :aria-describedby="helperId"
                        :id="inputId"
                        :placeholder="inputGroupData.placeholder || $t('Text')"
                        :status="inputGroupData.status"
                        :maxlength="inputGroupData.maxLength"
                        v-model="inputGroupData.value"
                        @input="validate()"
                    />
                </template>

                <div v-if="inputGroupData.maxLength" class="profile__helper-text-count">
                    <InputLimitHelper>
                        {{ charactersRemainingLabel }}
                    </InputLimitHelper>
                </div>

                <NebulaFormHelper
                    v-if="inputGroupData.status == 'error'"
                    :text="inputGroupData.errorString || $t('Required')"
                    icon="warning--filled"
                    :helperId="helperId"
                    :status="inputGroupData.status"
                    role="alert"
                />
                <NebulaFormHelper
                    v-if="inputGroupData.status == 'warning'"
                    :text="inputGroupData.warningString"
                    :helperId="helperId"
                    :status="inputGroupData.status"
                    role="alert"
                />
                <slot name="suggestion-chips" />
            </template>
        </NebulaFieldGroup>
    </div>
</template>

<script>
import {
    NebulaButton,
    NebulaCheckbox,
    NebulaDatepicker,
    NebulaFieldGroup,
    NebulaFormHelper,
    NebulaIcon,
    NebulaInput,
    NebulaLabel,
    NebulaPopover,
    NebulaSelect,
    NebulaSelectOption,
    NebulaTextarea,
    NebulaTitleTooltip,
    NebulaVueSelect,
} from '@discoveryedu/nebula-components';
import InputLimitHelper from '@/components/shared/forms/InputLimitHelper.vue';

/**
 * @deprecated Use Nebula form components (NebulaInput, NebulaTextarea, NebulaSelect, NebulaVueSelect,
 * NebulaCheckbox, NebulaDatepicker) directly inside a NebulaFieldGroup instead. This wrapper adds
 * indirection that obscures props/events and complicates validation work.
 */
export default {
    name: 'ProfileFormField',
    components: {
        NebulaDatepicker,
        NebulaFieldGroup,
        NebulaFormHelper,
        NebulaLabel,
        NebulaPopover,
        NebulaTextarea,
        NebulaIcon,
        NebulaInput,
        NebulaSelect,
        NebulaSelectOption,
        InputLimitHelper,
        NebulaVueSelect,
        NebulaCheckbox,
        NebulaTitleTooltip,
        NebulaButton,
    },
    emits: {
        updateMultiSelect: () => true,
        updateSelect: () => true,
        'update:inputGroupData': () => true,
        inputUpdated: () => true,
    },
    props: {
        inputGroupData: {
            type: Object,
        },
        inputType: {
            type: String,
            default: 'text',
            validator(val) {
                const allowed = ['text', 'select', 'textarea', 'multi-select', 'check-box', 'datepicker', 'timepicker'];
                return allowed.includes(val);
            },
        },
        flex: {
            type: Number,
            default: 1,
            validator(val) {
                const allowed = [1, 2, 3];
                return allowed.includes(val);
            },
        },
        name: {
            type: String,
            required: true,
        },
        validation: {
            type: Function,
        },
        selectOptions: {
            type: Array,
        },
        helperText: {
            type: String,
        },
        loading: {
            type: Boolean,
        },
        value: {
            type: Boolean,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: 'Select',
        },
        reduce: {
            type: Function,
        },
    },
    computed: {
        charactersRemainingLabel() {
            return this.$t('%(descriptionCharactersLeft)d/%(maxDescriptionLength)d characters remaining', {
                descriptionCharactersLeft: this.charsLeft,
                maxDescriptionLength: this.inputGroupData.maxLength,
            });
        },
        className() {
            return ['profile-form-field', `profile-form-field--flex-${this.flex}`];
        },
        showMultiSelect() {
            // selectOptions can be passed in without any data, we need to prevent
            // mounting this component until it arrives
            return !this.loading && this.selectOptions.length;
        },
        inputId() {
            return this.name;
        },
        hasTooltipContent() {
            return !!this.$slots['tooltip-content'];
        },
        helperId() {
            return `${this.name}-helper`;
        },
        inputComponent() {
            if (this.inputType === 'textarea') {
                return NebulaTextarea;
            }

            return NebulaInput;
        },
        multiSelectOptions() {
            return this.selectOptions.map((opt) => ({ ...opt, label: opt.text || opt.label }));
        },
        multiSelectValue() {
            const matches = this.inputGroupData.value.map((each) => this.multiSelectOptions.find((option) => option.value === each));
            return matches;
        },
        isSelect() {
            // return true;
            if (this.inputType === 'multi-select') {
                return false;
            }
            const hasOptions = !!this.selectOptions;
            return hasOptions || this.inputType === 'select';
        },
    },
    data() {
        return {
            charsLeft: this.inputGroupData.maxLength,
        };
    },
    methods: {
        datepickerClass() {
            const classes = ['profile-form-field__datepicker'];

            if (this.inputGroupData.status === 'error') {
                classes.push('profile-form-field__datepicker--error');
            }

            return classes.join(' ');
        },
        multiSelectTracker(options) {
            const values = options.map((each) => each.value || each);
            this.$emit('updateMultiSelect', { name: this.name, values });
            this.validate();
        },
        enforceCharLimit() {
            const { value, maxLength } = this.inputGroupData;

            // if value goes over the char limit, emit update to parent
            if (value.length > maxLength) {
                this.$emit('update:inputGroupData', {
                    ...this.inputGroupData,
                    value: value.slice(0, maxLength),
                });
            } else {
                // otherwise calculate the characters left
                this.charsLeft = maxLength - value.length;
            }
        },
        selectChange() {
            this.validate();
            this.$emit('updateSelect');
        },
        validate() {
            // run any passed-in validation func
            if (this.validation) {
                this.validation(this.name);
            }
            // if there's a max char, enforce it
            if (this.inputGroupData.maxLength) {
                this.enforceCharLimit();
            }
        },
        inputHandler(value) {
            this.$emit('inputUpdated', value);
        },
    },
    mounted() {
        if (this.inputType === 'datepicker') {
            const datePickerWrapper = document.querySelector('.profile-form-field__datepicker');
            const icon = document.querySelector('.profile-form-field__calendar-icon');

            if (datePickerWrapper && datePickerWrapper.firstChild && icon) {
                datePickerWrapper.firstChild.prepend(icon);
            }
        }
    },
    watch: {
        'inputGroupData.value': {
            handler(val) {
                if (this.inputGroupData.maxLength) {
                    const { maxLength } = this.inputGroupData;
                    const blank = val === null || val === undefined || val.length < 1;
                    this.charsLeft = blank ? maxLength : maxLength - val.length;
                }
            },
            immediate: true,
        },
    },
};
</script>

<style lang="stylus">
.profile-form-field {
    --nebula-input-max-width: 1000px;
    --nebula-select-max-width: 1000px;
    --nebula-textarea-max-width: 1000px;

    flex-basis: 100%;
    width: 100%;

    .nebula-popover {
        max-width: 320px;
        white-space: normal;
    }

    @media (max-width: $nebula-breakpoints-tablet-portrait) {
        --nebula-input-max-width: 100%;
        --nebula-select-max-width: 100%;
        --nebula-textarea-max-width: 100%;
        max-width: 100%;

        .nebula-popover {
            max-width: calc(100vw - ($nebula-space-4x * 2));
        }
    }

    .nebula-vue-select {
        max-width: var(--nebula-input-max-width);
    }

    &--flex-1 {
        @media (min-width: $nebula-breakpoints-tablet-portrait) {
            flex: 1;
        }
    }

    &--flex-2 {
        @media (min-width: $nebula-breakpoints-tablet-portrait) {
            flex: 2;
        }
    }

    &--flex-3 {
        @media (min-width: $nebula-breakpoints-tablet-portrait) {
            flex: 3;
        }
    }

    &__label-row {
        align-items: center;
        display: flex;
        gap: $nebula-space-half;

        .nebula-label {
            margin-bottom: 0;
        }
    }

    &__textarea {
        resize: none;
        width: 100%;
        max-width: 100%;
    }

    &__vue-select {
        max-width: unset;
        text-transform: capitalize;
    }

    &__datepicker {
        --nebula-date-picker-input-padding: 10px;
        position: relative;

        &.nebula-datepicker {
            max-width: 100%;
        }

        &--error {
            input {
                border-color: var(--nebula-select-border-color-error);
                box-shadow: var(--nebula-select-box-shadow-error);
            }
        }
    }

    &__calendar-icon {
        fill: $nebula-color-platform-interface-800;
        position: absolute;
        right: 16px;
        top: $nebula-space-1x;
    }

}

.careers-form-select {
    background: $nebula-color-platform-white;
    border-radius: $nebula-border-radius-small;
}

.tnc {
    &__group {
        display: flex;
        gap: $nebula-space-2x;

        .nebula-checkbox__container {
            margin: 0;
        }
    }

    &__checkbox {
        margin-bottom: 0;

        .nebula-checkbox__visual {
            border: 2px solid $nebula-color-platform-interface-1000;
        }
    }

    &__link {
        color: $nebula-color-platform-interactive-800;
        text-decoration: none;

        &:focus,
        &:hover {
            text-decoration: underline;
        }
    }
}
</style>
